import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

// Piezas compartidas de la vista del cliente (CC-CONSOLE #981): tarjeta, KPI y
// los estados de carga / error. Tokens semánticos (bg-card, text-content…).

export function Card({ title, children, className = "" }: { title?: string; children: ReactNode; className?: string }) {
  return (
    <section className={`rounded-2xl border border-line bg-card p-4 ${className}`}>
      {title && <h2 className="mb-3 text-sm font-semibold text-content">{title}</h2>}
      {children}
    </section>
  );
}

export function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-line bg-card p-4">
      <p className="text-xs text-content-secondary">{label}</p>
      <p className="mt-1 text-2xl font-bold text-content">{value}</p>
      {hint && <p className="mt-1 text-xs text-content-secondary">{hint}</p>}
    </div>
  );
}

/** Mensaje de cada código de error del backend; sin código, el genérico. */
const ERROR_KEYS: Record<string, string> = {
  CC_TENANT_VIEW_FORBIDDEN: "clientView.errors.forbidden",
  CC_TENANT_VIEW_NOT_CONFIGURED: "clientView.errors.notConfigured",
  CC_TENANT_VIEW_TOKEN_REJECTED: "clientView.errors.tokenRejected",
  CC_TENANT_VIEW_UNAVAILABLE: "clientView.errors.unavailable",
};

export function StateBox({ loading, error, onRetry }: { loading: boolean; error: string | null; onRetry?: () => void }) {
  const { t } = useTranslation("common");
  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-content-secondary" data-testid="client-view-loading">
        <i className="ri-loader-4-line animate-spin" /> {t("clientView.loading")}
      </div>
    );
  }
  if (!error) return null;
  return (
    <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-500" data-testid="client-view-error">
      <p>{t(ERROR_KEYS[error] ?? "clientView.errors.generic")}</p>
      {onRetry && error !== "CC_TENANT_VIEW_FORBIDDEN" && error !== "CC_TENANT_VIEW_NOT_CONFIGURED" && (
        <button type="button" onClick={onRetry} className="mt-2 rounded-lg border border-red-500/40 px-3 py-1 text-xs">
          {t("clientView.retry")}
        </button>
      )}
    </div>
  );
}

export function PageHeader({ title, subtitle, children }: { title: string; subtitle?: string; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-content">{title}</h1>
        {subtitle && <p className="text-sm text-content-secondary">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export const hours = (n: number | null | undefined) => `${(Number(n) || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })} h`;
/** Monto con su moneda. Un código no ISO (p. ej. «US$» de una importación) no rompe la página. */
export const money = (n: number | null | undefined, currency?: string | null) => {
  const code = typeof currency === "string" && /^[A-Z]{3}$/.test(currency) ? currency : "MXN";
  try {
    return (Number(n) || 0).toLocaleString("es-MX", { style: "currency", currency: code, maximumFractionDigits: 2 });
  } catch {
    return `${(Number(n) || 0).toFixed(2)} ${code}`;
  }
};
